import { apiClient } from '../api/client';
import { useSyncStore } from '../store/syncStore';
import type { QueuedAction } from '../store/syncStore';

const MAX_RETRIES = 3;
const RETRY_DELAY = 1000; // 1 second

class SyncService {
  private syncInProgress = false;
  
  /**
   * Initialize the sync service
   * Sets up online/offline event listeners
   */
  initialize() {
    // Listen for online/offline events
    window.addEventListener('online', this.handleOnline);
    window.addEventListener('offline', this.handleOffline);
    
    // Listen for custom sync trigger event
    window.addEventListener('online-sync-trigger', this.syncQueue);
    
    // Set initial online status
    useSyncStore.getState().setOnlineStatus(navigator.onLine);
    
    // Sync any pending actions if online
    if (navigator.onLine) {
      this.syncQueue();
    }
  }
  
  /**
   * Clean up event listeners
   */
  cleanup() {
    window.removeEventListener('online', this.handleOnline);
    window.removeEventListener('offline', this.handleOffline);
    window.removeEventListener('online-sync-trigger', this.syncQueue);
  }
  
  /**
   * Handle online event
   */
  private handleOnline = () => {
    console.log('Network connection restored');
    useSyncStore.getState().setOnlineStatus(true);
  };
  
  /**
   * Handle offline event
   */
  private handleOffline = () => {
    console.log('Network connection lost');
    useSyncStore.getState().setOnlineStatus(false);
  };
  
  /**
   * Sync all pending actions in the queue
   */
  syncQueue = async () => {
    const { queue, isOnline, isSyncing, startSync, endSync } = useSyncStore.getState();
    
    // Don't sync if offline or already syncing
    if (!isOnline || isSyncing || this.syncInProgress) {
      return;
    }
    
    // Don't sync if queue is empty
    const pendingActions = queue.filter(action => 
      action.status === 'pending' || action.status === 'failed'
    );
    
    if (pendingActions.length === 0) {
      return;
    }
    
    console.log(`Starting sync of ${pendingActions.length} actions`);
    this.syncInProgress = true;
    startSync();
    
    try {
      // Process actions sequentially to maintain order
      for (const action of pendingActions) {
        // Skip if already retried too many times
        if (action.retryCount >= MAX_RETRIES) {
          console.warn(`Action ${action.id} exceeded max retries, skipping`);
          continue;
        }
        
        await this.syncAction(action);
        
        // Add delay between requests to avoid overwhelming the server
        await this.delay(RETRY_DELAY);
      }
      
      // Update last sync time
      useSyncStore.getState().setLastSyncTime(Date.now());
      
      console.log('Sync completed successfully');
    } catch (error) {
      console.error('Sync failed:', error);
    } finally {
      this.syncInProgress = false;
      endSync();
    }
  };
  
  /**
   * Sync a single action
   */
  private async syncAction(action: QueuedAction): Promise<void> {
    const { updateActionStatus, removeFromQueue, incrementRetryCount } = useSyncStore.getState();
    
    try {
      console.log(`Syncing action ${action.id} (${action.type})`);
      updateActionStatus(action.id, 'syncing');
      
      // Make the API request
      const response = await apiClient.request({
        method: action.method,
        url: action.endpoint,
        data: action.data
      });
      
      // Mark as completed and remove from queue
      updateActionStatus(action.id, 'completed');
      
      // Remove after a short delay to allow UI to show success
      setTimeout(() => {
        removeFromQueue(action.id);
      }, 1000);
      
      console.log(`Action ${action.id} synced successfully`);
      
      // Dispatch event for UI updates
      window.dispatchEvent(new CustomEvent('sync-success', { 
        detail: { action, response } 
      }));
      
    } catch (error: any) {
      console.error(`Failed to sync action ${action.id}:`, error);
      
      incrementRetryCount(action.id);
      
      // Check if we should retry
      const updatedAction = useSyncStore.getState().queue.find(a => a.id === action.id);
      
      if (updatedAction && updatedAction.retryCount >= MAX_RETRIES) {
        // Max retries reached, mark as failed
        updateActionStatus(
          action.id, 
          'failed', 
          error.response?.data?.message || error.message || 'Sync failed'
        );
        
        console.warn(`Action ${action.id} failed after ${MAX_RETRIES} retries`);
      } else {
        // Will retry on next sync
        updateActionStatus(
          action.id, 
          'failed', 
          error.response?.data?.message || error.message || 'Sync failed, will retry'
        );
      }
      
      // Dispatch event for UI updates
      window.dispatchEvent(new CustomEvent('sync-error', { 
        detail: { action, error } 
      }));
    }
  }
  
  /**
   * Queue an action for later sync
   */
  queueAction(
    type: QueuedAction['type'],
    endpoint: string,
    method: QueuedAction['method'],
    data?: any
  ) {
    const { addToQueue } = useSyncStore.getState();
    
    addToQueue({
      type,
      endpoint,
      method,
      data
    });
    
    console.log(`Action queued: ${type} ${method} ${endpoint}`);
  }
  
  /**
   * Utility function to delay execution
   */
  private delay(ms: number): Promise<void> {
    return new Promise(resolve => setTimeout(resolve, ms));
  }
}

// Export singleton instance
export const syncService = new SyncService();
